import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Alert from "../components/Alert";
import Banner from "../components/Banner";
import Message from "../components/Message";
import ProductCard from "../components/ProductCard";
import SimpleAlert from "../components/SimpleAlert";
import { useAuth } from "../components/AuthProvider";

function isNullOrUndefined(value: any): boolean {
    return value === undefined || value === null;
}
function HomePage({
    portalUrl,
    productIds,
    subProductIds,
    user
}: {
    portalUrl: string;
    productIds: string[];
    subProductIds: { productId: string; editorsProductId?: string; viewersProductId?: string }[];
    user?: any;
}) {
    // Authentication
    const { token, isSignedIn, appAuthId, autoAuthorize } = useAuth();

    const [working, setWorking] = useState(false);
    const [products, setProducts] = useState<any>([]);
    const [subProducts, setSubProducts] = useState<any>([]);
    const [self, setSelf] = useState<any>({});
    const [syncError, setSyncError] = useState<any>(null);
    const oAuthPath = window.location.href.split("#")[0].split("?")[0],
        oAuthUrl: string = `${portalUrl}/oauth2/authorize?client_id=${appAuthId}&response_type=token&redirect_uri=${oAuthPath}`;

    useEffect(() => {
        async function loadProductDetails() {
            setWorking(true);
            try {
                const portalSelf: any = await fetch(
                    `${portalUrl}/portals/self?f=json&token=${token}`
                ).then((rsp) => {
                    return rsp.json();
                });
                if (portalSelf.error !== undefined) {
                    setSelf(null);
                    throw new Error(portalSelf.error.message);
                }
                setSelf(portalSelf);
                const bought: any = await fetch(
                        `${portalUrl}/portals/${portalSelf.id}/purchases?f=json&status=active&token=${token}`
                    ).then((rsp) => {
                        return rsp.json();
                    }),
                    productSet: any = [];

                const subProductSet: any = [];
                if (bought.error !== undefined) throw new Error(bought.error.message);
                for (let id of productIds) {
                    const details: any = await fetch(
                            `${portalUrl}/content/listings/${id.split(":")[0]}?f=json`
                        ).then((rsp) => {
                            return rsp.json();
                        }),
                        purchase: any =
                            bought.purchases !== undefined
                                ? bought.purchases.find(
                                      (p: { provision: { itemId: undefined } | undefined }) =>
                                          p.provision !== undefined &&
                                          p.provision.itemId !== undefined &&
                                          p.provision.itemId === id.split(":")[0]
                                  )
                                : {};
                    if (purchase !== undefined && purchase.provision !== undefined)
                        productSet.push(details);
                }

                for (let subProd of subProductIds) {
                    // Get details of subproducts and add to setSubProducts so they can be passed to the licensing page
                    const purchase: any =
                        bought.purchases !== undefined
                            ? bought.purchases.find(
                                  (p: { provision: { itemId: undefined } | undefined }) =>
                                      p?.provision?.itemId === subProd.editorsProductId ||
                                      p?.provision?.itemId === subProd.viewersProductId
                              )
                            : {};
                    // Got a purchase, therefore we can add the sub-product definition
                    if (purchase?.provision !== undefined) {
                        subProductSet.push(subProd);
                    }
                }

                for (let product of productSet) {
                    const filteredPurchases = bought.purchases.filter(
                        (purchase: { provision: { itemId: any } }) =>
                            purchase.provision.itemId === product.itemId
                    );

                    product.licenseExpires =
                        filteredPurchases[0].provision.endDate > 0
                            ? new Date(filteredPurchases[0].provision.endDate)
                                  .toISOString()
                                  .substring(0, 10)
                            : "[never expires]";
                }

                setWorking(false);
                setProducts(productSet);
                setSubProducts(subProductSet);
            } catch (syncErr) {
                setSyncError(syncErr);
                setWorking(false);
            }
        }
        if (isSignedIn) loadProductDetails();
        else if (autoAuthorize) window.location.href = oAuthUrl;
    }, [isSignedIn]);

    const { t } = useTranslation("common");
    const productList = products.map((listing: any, pi: number) => {
        const subProductRef: {
            productId: string;
            editorsProductId: string;
            viewersProductId?: string;
        } = subProducts.find((subProd: any) => {
            return subProd.productId === listing.itemId;
        });
        return <ProductCard key={pi} listing={listing} subProducts={subProductRef} />;
    });

    return (
        <div className="home-page">
            {syncError !== undefined && syncError !== null && (
                <div className="max-w-screen-xl mx-auto mx-auto absolute mt-6 container -translate-x-1/2 left-1/2 transform shadow-md">
                    <Alert message={syncError} />
                </div>
            )}
            {working ? (
                <div className="masker">
                    <div className="h-48">
                        <div className="px-4 pt-12 pb-12">
                            <Message />
                        </div>
                    </div>
                </div>
            ) : (
                <div className="page-content">
                    {isSignedIn && !isNullOrUndefined(user) && !isNullOrUndefined(self) ? (
                        <>
                            <Banner />

                            {productList.length > 0 ? (
                                <div className="w-100 px-8 md:px-auto">
                                    <div className="max-w-screen-xl mx-auto h-16 mx-auto md:px-4 container">
                                        <h2 className="font-[var(--calcite-font-weight-normal)] max-w-screen-xl mx-auto pt-8 text-[var(--calcite-color-text-1)] text-[1.625rem]">
                                            <>{t("home.yourproducts")}</>
                                        </h2>
                                        <div className="grid lg:grid-cols-2 max-w-screen-xl py-8 mx-auto lg:gap-8 space-y-4 lg:space-y-0 xl:gap-8 lg:py-8 mx-auto mb-10">
                                            {productList}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="-translate-x-1/2 absolute left-1/2 lg:w-1/2 transform w-full -mt-8 shadow-lg">
                                    <SimpleAlert
                                        message={t("home.noproducts", {
                                            org: self.name,
                                            marketplace: t("platform.marketplace")
                                        })}
                                    />
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            <Banner oAuthUrl={oAuthUrl} />
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

export default HomePage;
